/**
 * UseInCampaignSheet — Templates Manager · May 2026.
 *
 * Lightweight bottom-right sheet that lets a sales user attach a
 * published template to one of their existing campaigns in one click.
 * Backend wires this through `POST /api/templates/{id}/use-in-campaign`.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Megaphone, Loader2, ArrowRight } from "lucide-react";
import { API, useAuth } from "@/App";

export default function UseInCampaignSheet({ template, onClose, onAttached }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [stepLabel, setStepLabel] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/campaigns`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const rows = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        setCampaigns(rows);
      } catch {
        toast.error("Couldn't load campaigns");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const attach = async (campaign) => {
    setBusyId(campaign.id);
    try {
      await axios.post(
        `${API}/templates/${template.id}/use-in-campaign`,
        {
          type: template.type,
          campaign_id: campaign.id,
          step_label: stepLabel || `${template.type[0].toUpperCase()}${template.type.slice(1)} — ${template.name}`,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Attached "${template.name}" to ${campaign.name}`);
      if (onAttached) onAttached(campaign);
      onClose();
    } catch (e) {
      toast.error("Failed: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      data-testid="use-in-campaign-sheet"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
        display: "flex", alignItems: "flex-end", justifyContent: "flex-end",
        zIndex: 10001,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 420, maxWidth: "100vw", height: "100vh",
          background: "var(--surface, #fff)",
          borderLeft: "0.5px solid var(--border)",
          display: "flex", flexDirection: "column",
          fontFamily: '"DM Sans", -apple-system, sans-serif',
        }}
      >
        <header
          style={{
            padding: "14px 18px", borderBottom: "0.5px solid var(--border)",
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div
              style={{
                width: 30, height: 30, borderRadius: 8,
                background: "rgba(139,92,246,0.10)", color: "#7c3aed",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <Megaphone style={{ width: 14, height: 14 }} />
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 650 }}>Use in campaign</div>
              <div style={{ fontSize: 10.5, color: "var(--text-tertiary)" }}>
                Attach "{template.name}" to a campaign step
              </div>
            </div>
          </div>
          <button
            data-testid="use-in-campaign-close"
            onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer" }}
          >
            <X style={{ width: 14, height: 14 }} />
          </button>
        </header>

        <div style={{ padding: 16, borderBottom: "0.5px solid var(--border)" }}>
          <label style={{ display: "block" }}>
            <div
              style={{
                fontSize: 10.5, fontWeight: 600, color: "var(--text-tertiary)",
                textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4,
              }}
            >
              Step label (optional)
            </div>
            <input
              data-testid="use-in-campaign-step-label"
              value={stepLabel}
              onChange={(e) => setStepLabel(e.target.value)}
              placeholder="e.g. Day 3 — Cold outreach"
              style={{
                width: "100%", padding: "8px 11px",
                border: "0.5px solid var(--border)", borderRadius: 8,
                fontSize: 12.5, background: "var(--surface,#fff)",
                outline: "none", boxSizing: "border-box", fontFamily: "inherit",
              }}
            />
          </label>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: 8 }}>
          {loading && (
            <div style={{ padding: 24, textAlign: "center", color: "var(--text-tertiary)", fontSize: 12 }}>
              <Loader2 style={{ width: 14, height: 14, animation: "spin 0.8s linear infinite" }} />
            </div>
          )}
          {!loading && campaigns.length === 0 && (
            <div
              data-testid="use-in-campaign-empty"
              style={{
                padding: 28, textAlign: "center", color: "var(--text-tertiary)",
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600 }}>No campaigns yet</div>
              <div style={{ fontSize: 11, marginTop: 4 }}>
                Create a campaign first, then attach templates as steps.
              </div>
            </div>
          )}
          {!loading && campaigns.map((c) => (
            <button
              key={c.id}
              data-testid={`use-in-campaign-row-${c.id}`}
              disabled={busyId === c.id}
              onClick={() => attach(c)}
              style={{
                display: "flex", alignItems: "center", gap: 8,
                width: "100%", padding: "10px 12px",
                border: "0.5px solid transparent",
                borderRadius: 10, background: "transparent",
                cursor: busyId === c.id ? "wait" : "pointer",
                fontFamily: "inherit", textAlign: "left",
                opacity: busyId && busyId !== c.id ? 0.4 : 1,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--page-bg,#f5f5f7)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text-primary)" }}>{c.name}</div>
                <div style={{ fontSize: 10.5, color: "var(--text-tertiary)" }}>
                  {(c.template_steps?.length || 0)} steps · {c.status || "draft"}
                </div>
              </div>
              {busyId === c.id
                ? <Loader2 style={{ width: 13, height: 13, animation: "spin 0.8s linear infinite", color: "var(--text-tertiary)" }} />
                : <ArrowRight style={{ width: 13, height: 13, color: "#185FA5" }} />}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
